// ==================== GURUKUL WORLD ====================
// Gurukul opens as its own world (like Finance): own sidebar, a View switcher (Learner / Manager /
// Admin — decided by the person's roles), a Learner switcher in the Learner view, and a menu that
// comes from the gurukul_menu_items table. To port a module: add a page below (or reuse a card page)
// and one row in that table — nothing else.
import { renderOkShell, initOkShell } from './ok-shell.js';
import { navigateOk } from '../utils/okAccess.js';
import { showToast, showAlert } from '../components/toasts.js';
import { getGurukulCardsHtml, initGurukulCards, setGurukulRoleFlags, GURUKUL_CARD_PAGES } from './gurukul-admin.js';
import { getGurukulRequestsPage, initGurukulRequestsPage } from './gurukul-requests.js';
import { getGurukulDocumentsPage, initGurukulDocumentsPage } from './gurukul-documents.js';
import { getGurukulCatalogPage, initGurukulCatalogPage } from './gurukul-catalog.js';
import { getLmsAccessPage, initLmsAccessPage, getLmsAdminPage, initLmsAdminPage } from './gurukul-lms.js';
import { getLmsCompletionsPage, initLmsCompletionsPage } from './gurukul-lms-completions.js';
import { getVolunteerLmsAccessPage, initVolunteerLmsAccessPage } from './gurukul-volunteer-lms.js';
import { getVolunteerOnboardingPage, initVolunteerOnboardingPage, getVolunteersAdminPage, initVolunteersAdminPage } from './gurukul-volunteer.js';
import { getGurukulEnrollPage, initGurukulEnrollPage } from './gurukul-enroll.js';
import { getCoordinatorPage, initCoordinatorPage, getContactPage, initContactPage, getMessagesAdminPage, initMessagesAdminPage, getIssuesPage, initIssuesPage } from './gurukul-support.js';
import { getGurukulConsentsPage, initGurukulConsentsPage, getGurukulLegalAdminPage, initGurukulLegalAdminPage } from './gurukul-legal.js';

function esc(text) {
  return String(text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
async function api(path, options = {}) {
  const res = await fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

const store = {
  get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
  set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { /* optional */ } },
};

// Old #hash links (from notifications / bookmarks) → world pages
const HASH_PAGES = {
  requests: 'requests', 'add-user-requests': 'requests', 'guardian-requests': 'requests', 'retire-requests': 'requests',
  teams: 'teams', learners: 'learners', 'lms-updates': 'lms-updates', 'approval-roles': 'settings', 'auto-add': 'settings',
};

const navItemStyle = 'display:flex;align-items:center;gap:10px;padding:9px 16px;cursor:pointer;border-left:3px solid transparent;font-size:0.88rem;';

export async function renderGurukulWorld(app, subPage) {
  app.innerHTML = renderOkShell({
    title: 'Gurukul', activePath: '/gurukul',
    worldNav: '<div id="gkWorldNav" style="padding:12px 16px;">Loading…</div>',
    mainHtml: '<div id="gkWorldContent"><p class="empty-state">Loading…</p></div>',
  });
  initOkShell();

  let cfg;
  try { cfg = await api('/api/gurukul/world'); }
  catch (err) { document.getElementById('gkWorldContent').innerHTML = `<p class="empty-state">${esc(err.message)}</p>`; return; }
  setGurukulRoleFlags(cfg.flags);

  if (!cfg.views.length) {
    document.getElementById('gkWorldNav').innerHTML = '';
    document.getElementById('gkWorldContent').innerHTML = '<h1 class="page-title">Gurukul</h1><p class="page-intro">You do not have a Gurukul role yet. Ask a Gurukul admin to add you.</p>';
    return;
  }

  // GUR-14b "sign before access": until the person has signed, Gurukul shows only the sign-up.
  if (cfg.onboarding?.blocked) { await renderSignupGate(cfg); return; }
  if (subPage === 'volunteer-signup' && cfg.onboarding?.staff) {
    document.getElementById('gkWorldNav').innerHTML = '';
    const box = document.getElementById('gkWorldContent');
    box.innerHTML = getVolunteerOnboardingPage();
    await initVolunteerOnboardingPage(() => navigateOk('/gurukul'));
    return;
  }

  // page + view
  if (!subPage && window.location.hash) subPage = HASH_PAGES[window.location.hash.replace('#', '')] || '';
  const codes = cfg.views.map(v => v.code);
  const pageIn = v => (cfg.menu[v] || []).some(i => i.page_key === subPage);
  let view = store.get('gk_view');
  if (!codes.includes(view)) view = cfg.default_view || codes[codes.length - 1];
  if (subPage && !pageIn(view)) { const other = codes.find(pageIn); if (other) view = other; }
  store.set('gk_view', view);
  const menu = cfg.menu[view] || [];
  const page = menu.find(i => i.page_key === subPage) ? subPage : (menu[0]?.page_key || '');
  const item = menu.find(i => i.page_key === page);

  // learner switcher (Learner view)
  let learnerId = store.get('gk_learner');
  if (!cfg.learners.some(l => l.id === learnerId)) learnerId = cfg.learners[0]?.id || null;
  if (learnerId) store.set('gk_learner', learnerId);

  const nav = document.getElementById('gkWorldNav');
  nav.style.padding = '0';
  nav.innerHTML = `
    <div style="padding:10px 16px;border-bottom:1px solid rgba(255,255,255,0.08);">
      <div style="font-size:1rem;font-weight:700;margin-bottom:8px;">Gurukul</div>
      ${cfg.views.length > 1 ? `<label style="font-size:0.7em;text-transform:uppercase;opacity:0.7;letter-spacing:0.5px;">View</label>
        <select id="gkViewSel" style="width:100%;margin-bottom:8px;">${cfg.views.map(v => `<option value="${v.code}"${v.code === view ? ' selected' : ''}>${esc(v.label)}</option>`).join('')}</select>` : `<div style="font-size:0.75rem;opacity:0.7;">${esc(cfg.views[0].label)} view</div>`}
      ${view === 'learner' && cfg.learners.length ? `<label style="font-size:0.7em;text-transform:uppercase;opacity:0.7;letter-spacing:0.5px;">Learner</label>
        <select id="gkLearnerSel" style="width:100%;">${cfg.learners.map(l => `<option value="${l.id}"${l.id === learnerId ? ' selected' : ''}>${esc(l.learner_id)}</option>`).join('')}</select>` : ''}
    </div>
    <div style="padding:8px 0;display:flex;flex-direction:column;gap:2px;">
      ${menu.map(i => `<div class="sidebar-item${i.page_key === page ? ' active' : ''}" data-gk-page="${esc(i.page_key)}" style="${navItemStyle}">
          <i class="${esc(i.icon)}" style="width:18px;text-align:center;"></i><span style="flex:1;">${esc(i.label)}</span>${i.wave ? '<span class="nav-soon">Soon</span>' : ''}</div>`).join('')}
      <div class="sidebar-item" data-ok-nav="/kompass" style="${navItemStyle}margin-top:10px;border-top:1px solid rgba(255,255,255,0.08);">
        <i class="fa-solid fa-compass" style="width:18px;text-align:center;"></i><span>Kompass — work items</span></div>
      <div class="sidebar-item" data-ok-nav="/" style="${navItemStyle}">
        <i class="fa-solid fa-arrow-left" style="width:18px;text-align:center;"></i><span>OneKailasa home</span></div>
    </div>`;

  // Cross-app links use the shell's own handler (wired by initOkShell before the nav existed) → wire here
  nav.querySelectorAll('[data-ok-nav]').forEach(el => el.addEventListener('click', () => navigateOk(el.dataset.okNav)));
  nav.querySelectorAll('[data-gk-page]').forEach(el => el.addEventListener('click', () => {
    document.getElementById('sidebar')?.classList.remove('active');
    document.querySelector('.overlay')?.classList.remove('active');
    navigateOk('/gurukul/' + el.dataset.gkPage);
  }));
  document.getElementById('gkViewSel')?.addEventListener('change', e => {
    store.set('gk_view', e.target.value);
    navigateOk('/gurukul/' + (cfg.menu[e.target.value][0]?.page_key || ''));
  });
  document.getElementById('gkLearnerSel')?.addEventListener('change', e => {
    store.set('gk_learner', e.target.value);
    navigateOk('/gurukul/learner-home');
  });

  await mountPage(page, item, view, learnerId);
  // Yearly volunteer re-acknowledgment coming up (30 days before): a reminder at the top of every page.
  if (cfg.onboarding?.reminder) {
    const note = document.createElement('div');
    note.className = 'card';
    note.style.cssText = 'border-left:4px solid #D4A017;';
    note.innerHTML = `<p style="margin:0;">${esc(cfg.onboarding.reminder)} <a href="#" id="gkResignLink">Re-sign now</a></p>`;
    document.getElementById('gkWorldContent').prepend(note);
    document.getElementById('gkResignLink').addEventListener('click', e => { e.preventDefault(); navigateOk('/gurukul/volunteer-signup'); });
  }
}

/** Only the sign-up is available: volunteer onboarding for staff, then (for parents) the enrollment consent. */
async function renderSignupGate(cfg) {
  const ob = cfg.onboarding;
  const nav = document.getElementById('gkWorldNav');
  nav.style.padding = '0';
  nav.innerHTML = `
    <div style="padding:10px 16px;border-bottom:1px solid rgba(255,255,255,0.08);"><div style="font-size:1rem;font-weight:700;">Gurukul</div></div>
    <div style="padding:8px 0;display:flex;flex-direction:column;gap:2px;">
      <div class="sidebar-item active" style="${navItemStyle}"><i class="fa-solid fa-file-signature" style="width:18px;text-align:center;"></i><span>Sign-up</span></div>
      <div class="sidebar-item" data-ok-nav="/" style="${navItemStyle}margin-top:10px;border-top:1px solid rgba(255,255,255,0.08);">
        <i class="fa-solid fa-arrow-left" style="width:18px;text-align:center;"></i><span>OneKailasa home</span></div>
    </div>`;
  nav.querySelectorAll('[data-ok-nav]').forEach(el => el.addEventListener('click', () => navigateOk(el.dataset.okNav)));
  const box = document.getElementById('gkWorldContent');
  const intro = '';
  if (store.get('gk_signup_welcomed') !== '1') {
    store.set('gk_signup_welcomed', '1');
    showAlert('Welcome to Gurukul. Before you can use it, please read and sign the documents. Everything else opens as soon as you have signed.', 'info');
  }
  if (ob.next === 'volunteer') {
    box.innerHTML = intro + getVolunteerOnboardingPage();
    await initVolunteerOnboardingPage(() => navigateOk('/gurukul'));
  } else if (ob.next === 'parent_enroll') {
    box.innerHTML = intro + getGurukulEnrollPage();
    await initGurukulEnrollPage();
  } else {
    box.innerHTML = intro + getGurukulConsentsPage();
    await initGurukulConsentsPage(cfg.learners[0]?.id || null);
  }
}

async function mountPage(page, item, view, learnerId) {
  const box = document.getElementById('gkWorldContent');
  if (!item) { box.innerHTML = '<p class="empty-state">Nothing here yet.</p>'; return; }

  if (item.wave) {
    box.innerHTML = `<h1 class="page-title">${esc(item.label)}</h1>
      <div class="card"><h2>Coming in wave ${esc(item.wave)}</h2>
      <p class="page-intro">${esc(item.description || '')}</p>
      <p class="page-intro">This page is part of the plan to bring everything from the old eGurukul into OneKailasa. See <strong>GURUKUL_MASTER_PLAN.md</strong>.</p></div>`;
    return;
  }
  if (page === 'requests') { box.innerHTML = getGurukulRequestsPage(); await initGurukulRequestsPage(); return; }
  if (page === 'documents') { box.innerHTML = getGurukulDocumentsPage(); await initGurukulDocumentsPage(); return; }
  if (page === 'catalog') { box.innerHTML = getGurukulCatalogPage(); await initGurukulCatalogPage(); return; }
  if (page === 'lms-access') { box.innerHTML = getLmsAccessPage(); await initLmsAccessPage(learnerId); return; }
  if (page === 'volunteers' && view === 'admin') { box.innerHTML = getVolunteersAdminPage(); await initVolunteersAdminPage(); return; }
  if (page === 'lms-provisioning') { box.innerHTML = getLmsAdminPage(); await initLmsAdminPage(); return; }
  if (page === 'lms-completions') { box.innerHTML = getLmsCompletionsPage(); await initLmsCompletionsPage(); return; }
  if (page === 'volunteer-lms-access') { box.innerHTML = getVolunteerLmsAccessPage(); await initVolunteerLmsAccessPage(); return; }
  if (page === 'consents') { box.innerHTML = getGurukulConsentsPage(); await initGurukulConsentsPage(learnerId); return; }
  if (page === 'legal') { box.innerHTML = getGurukulLegalAdminPage(); await initGurukulLegalAdminPage(); return; }
  if (page === 'enroll') { box.innerHTML = getGurukulEnrollPage(); await initGurukulEnrollPage(); return; }
  if (page === 'coordinator') { box.innerHTML = getCoordinatorPage(); await initCoordinatorPage(learnerId); return; }
  if (page === 'contact') { box.innerHTML = getContactPage(); await initContactPage(); return; }
  if (page === 'messages') { box.innerHTML = getMessagesAdminPage(); await initMessagesAdminPage(); return; }
  if (page === 'issues') { box.innerHTML = getIssuesPage(view); await initIssuesPage(view); return; }
  if (page === 'learner-home') { await mountLearnerHome(box, learnerId); return; }
  if (page === 'dashboard') { await mountDashboard(box, view); return; }
  if (GURUKUL_CARD_PAGES.has(page)) {
    box.innerHTML = `<h1 class="page-title">${esc(item.label)}</h1>` + getGurukulCardsHtml();
    await initGurukulCards(page);
    return;
  }
  box.innerHTML = `<h1 class="page-title">${esc(item.label)}</h1><p class="empty-state">This page is not built yet.</p>`;
}

async function mountLearnerHome(box, learnerId) {
  if (!learnerId) {
    box.innerHTML = `<h1 class="page-title">My Learner</h1><div class="card"><p class="page-intro">There is no learner on your account yet. Enroll one under <strong>Family &amp; guardians</strong>.</p></div>`;
    return;
  }
  box.innerHTML = '<h1 class="page-title">My Learner</h1><p class="empty-state">Loading…</p>';
  try {
    const d = await api('/api/gurukul/world?learner=' + encodeURIComponent(learnerId));
    box.innerHTML = `
      <h1 class="page-title">${esc(d.learner.learner_id)}</h1>
      <div class="card"><h2>Overview</h2>
        <div class="table-container"><table class="table-stack-mobile"><tbody>
          <tr><th style="width:34%;text-align:left;">Learner ID</th><td data-label="Learner ID">${esc(d.learner.learner_id)}</td></tr>
          <tr><th style="text-align:left;">Status</th><td data-label="Status">${esc(d.learner.status)}</td></tr>
          <tr><th style="text-align:left;">Team</th><td data-label="Team">${esc(d.learner.team || 'not placed yet')}</td></tr>
          <tr><th style="text-align:left;">Enrolled</th><td data-label="Enrolled">${d.learner.enrolled ? new Date(d.learner.enrolled).toLocaleDateString() : '—'}</td></tr>
          ${d.lms_updates_pending ? '<tr><th style="text-align:left;">LMS</th><td data-label="LMS">An LMS update is pending for this learner</td></tr>' : ''}
        </tbody></table></div></div>
      <div class="card"><h2>Coordinator</h2>
        ${d.coordinators.length ? d.coordinators.map(c => `<p>${esc(c.name)} — ${esc(c.email)}${c.primary ? ' <em>(primary)</em>' : ''}</p>`).join('') : '<p class="page-intro">A coordinator will be assigned once the learner is placed in a team.</p>'}</div>
      <div class="card"><h2>Guardians</h2>
        ${d.guardians.map(g => `<p>${esc(g.name)} — ${esc(g.relationship || '')}${g.primary ? ' <em>(primary)</em>' : ''}${g.you ? ' <em>(you)</em>' : ''}</p>`).join('')}</div>`;
  } catch (err) {
    box.innerHTML = `<h1 class="page-title">My Learner</h1><p class="empty-state">${esc(err.message)}</p>`;
  }
}

async function mountDashboard(box, view) {
  box.innerHTML = '<h1 class="page-title">Dashboard</h1><p class="empty-state">Loading…</p>';
  try {
    const s = await api('/api/gurukul/world?stats=1');
    const tile = (label, n, page, warn) => `<article class="card data-card--clickable" data-go="${page}" style="cursor:pointer;padding:16px 18px;">
      <div class="muted" style="font-size:13px;">${esc(label)}</div>
      <div style="font-size:30px;font-weight:700;margin-top:6px;${warn ? 'color:var(--warning);' : ''}">${n}</div></article>`;
    const attention = [
      s.lms_pending ? { text: `${s.lms_pending} LMS update${s.lms_pending === 1 ? '' : 's'} pending`, sub: 'Learners whose team changed and need an LMS update', page: view === 'admin' ? 'lms-updates' : 'my-team' } : null,
      s.open_requests ? { text: `${s.open_requests} open request${s.open_requests === 1 ? '' : 's'}`, sub: s.scoped ? 'Requests from your teams' : 'Requests waiting for review', page: 'requests' } : null,
    ].filter(Boolean);
    box.innerHTML = `<div style="display:flex;flex-direction:column;gap:24px;">
      <header>
        <div class="muted" style="font-size:13px;">Gurukul · ${s.scoped ? 'Your teams' : 'All of Gurukul'}</div>
        <h1 class="page-title" style="margin:4px 0 0;">Nithyanandam, ${esc(s.name || '')}</h1>
      </header>
      <section aria-label="Key numbers" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:14px;">
        ${tile(s.scoped ? 'My teams' : 'Teams', s.teams, view === 'admin' ? 'teams' : 'my-team')}
        ${tile('Active learners', s.learners, view === 'admin' ? 'learners' : 'my-team')}
        ${tile(s.scoped ? 'Parents in my teams' : 'Parents', s.people, view === 'admin' ? 'learners' : 'my-team')}
        ${tile(s.scoped ? 'My open requests' : 'Open requests', s.open_requests, 'requests')}
        ${tile('LMS updates pending', s.lms_pending, view === 'admin' ? 'lms-updates' : 'my-team', s.lms_pending > 0)}
      </section>
      <div style="display:grid;grid-template-columns:minmax(0,2fr) minmax(0,1fr);gap:20px;align-items:start;">
        <section class="card">
          <h2 style="margin:0 0 4px;font-size:17px;">Needs your attention</h2>
          <p class="muted" style="margin:0 0 14px;">Items waiting on you</p>
          ${attention.length ? `<ul style="list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:10px;">
            ${attention.map(a => `<li style="display:flex;justify-content:space-between;align-items:center;gap:12px;padding:12px 14px;border:1px solid var(--border);border-radius:10px;background:var(--bg-secondary);">
              <span><strong>${esc(a.text)}</strong><br><small class="muted">${esc(a.sub)}</small></span>
              <button type="button" class="btn-ghost" data-go="${a.page}">Open</button></li>`).join('')}
          </ul>` : '<p class="empty-state" style="margin:0;">Nothing waiting on you right now.</p>'}
        </section>
        <aside style="display:flex;flex-direction:column;gap:20px;">
          <section class="card">
            <h2 style="margin:0 0 12px;font-size:17px;">Quick actions</h2>
            <div style="display:flex;flex-direction:column;gap:8px;">
              <button type="button" class="btn-ghost" data-go="add-user">Add a user</button>
              ${view === 'admin' ? '<button type="button" class="btn-ghost" data-go="lms-provisioning">LMS accounts</button>' : ''}
              <button type="button" class="btn-ghost" data-go="volunteers">Volunteers</button>
            </div>
          </section>
        </aside>
      </div>
    </div>`;
    box.querySelectorAll('[data-go]').forEach(el => el.addEventListener('click', () => navigateOk('/gurukul/' + el.dataset.go)));
  } catch (err) { showToast(err.message, 'error'); }
}
